import { Dialog } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import * as z from 'zod';

import { MatchAlertChannelsContent } from '#modules/finder/features/posts/components/Post/MatchAlertChannels';
import { MatchAlertsRow } from '#modules/finder/features/posts/components/Post/MatchAlertsRow';
import { NotificationChannels } from '#modules/user/features/profile/components/Settings/NotificationChannels';
import { NotificationSettingsPage } from '#modules/user/features/profile/components/Settings/NotificationSettingsPage';
import { SettingsLayout } from '#modules/user/features/profile/components/Settings/SettingsLayout';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { usePostNotificationNavigation } from '#features/notifications/hooks/usePostNotificationNavigation';
import type { PushDeviceStatus } from '#features/notifications/hooks/usePushDeviceStatus';
import type { useWebPushAsk } from '#features/notifications/hooks/useWebPushAsk';
import { Button, SectionHeading } from '#features/ui';
import { DialogContent, DialogHeader, DialogRoot } from '#features/ui/components/Dialog';

const schema = z.object({
  lang: z.enum(['ru', 'en']).default('ru'),
  platform: z.enum(['web', 'android', 'ios']).default('web'),
  connection: z.enum(['linked', 'unlinked', 'unknown']).default('linked'),
  surface: z.enum(['intro', 'settings', 'channels', 'general']).default('intro'),
  subscriptionState: z.enum(['ready', 'loading', 'error']).default('ready'),
  destination: z.enum(['preview', 'live']).default('preview'),
  device: z
    .enum(['ok', 'unsupported', 'denied', 'prompt', 'no-token-on-server', 'unknown'])
    .optional(),
  state: z
    .enum([
      'normal',
      'off',
      'expired',
      'expiring',
      'loading',
      'error',
      'blocked',
      'unregistered',
      'unsupported',
      'no-discord',
      'native-denied',
      'partial',
      'prompt',
      'unknown',
      'push-off',
      'bell-off',
      'discord-off',
    ])
    .default('normal'),
});
export const Route = createFileRoute('/{-$locale}/_layout/preview/match-alerts')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: zodValidator(schema),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: MatchAlertsPreviewRoute,
});
function MatchAlertsPreviewRoute() {
  const search = Route.useSearch();
  return <Preview key={JSON.stringify(search)} {...search} />;
}
const noop = () => {};
const previewPush = { state: 'ok', askable: false, allow: noop, later: noop } as ReturnType<
  typeof useWebPushAsk
>;

function Preview(props: z.infer<typeof schema>) {
  const [i18n] = useState(() =>
    setupI18n({ locale: props.lang, messages: { en: enMessages, ru: ruMessages } })
  );
  const [client] = useState(() => {
    const value = new QueryClient();
    const queries = value.defaultQueryOptions.bind(value);
    const mutations = value.defaultMutationOptions.bind(value);
    // All query and mutation paths stay inside this fixture, including manual retries.
    value.defaultQueryOptions = (options) => ({
      ...queries(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
      queryFn: async () => {
        throw new Error('This preview is read-only');
      },
    });
    value.defaultMutationOptions = (options) => ({
      ...mutations(options),
      retry: false,
      mutationFn: async () => {
        throw new Error('This preview is read-only');
      },
    });
    value.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: 'preview-user',
      email: 'preview@example.test',
      emailConfirmed: true,
      oauth:
        props.state === 'no-discord' || props.connection === 'unlinked'
          ? {}
          : { discord: { id: 'preview-discord' } },
      pushToken: [],
      timeFormat: 'TWENTY_FOUR',
      timeZone: 0,
    });
    if (props.subscriptionState !== 'loading')
      value.setQueryData(['post-match-alerts', 'preview-post'], {
        matchAlertsEnabled: props.state !== 'off' && props.state !== 'expired',
        matchAlertsExpiresAt: new Date(
          Date.now() +
            (props.state === 'expired'
              ? -86400000
              : props.state === 'expiring'
                ? 86400000
                : 5 * 86400000)
        ).toISOString(),
      });
    if (props.subscriptionState === 'error') {
      value
        .getQueryCache()
        .find({ queryKey: ['post-match-alerts', 'preview-post'] })
        ?.setState({
          data: undefined,
          status: 'error',
          error: new Error('Fixture subscription error'),
        });
    }
    const prefs = ['BELL', 'PUSH', 'DISCORD_DM'].map((channelType) => ({
      eventType: 'POST_MATCH_FOUND',
      channelType,
      enabled:
        props.state !== 'off' &&
        !(props.state === 'push-off' && channelType === 'PUSH') &&
        !(props.state === 'bell-off' && channelType === 'BELL') &&
        !(props.state === 'discord-off' && channelType === 'DISCORD_DM'),
    }));
    if (props.state !== 'loading') value.setQueryData(['notification-preferences'], prefs);
    if (props.state === 'error')
      value
        .getQueryCache()
        .find({ queryKey: ['notification-preferences'] })
        ?.setState({ data: undefined, status: 'error', error: new Error('Fixture error') });
    value.setQueryData(
      ['notification-channel-summary'],
      ['EMAIL', 'PUSH', 'DISCORD_DM'].map((channelType) => ({
        channelType,
        enabled: props.state !== 'partial' && props.state !== 'off',
        partiallyEnabled: props.state === 'partial',
      }))
    );
    value.setQueryData(['notification-marketing-emails'], true);
    return value;
  });
  return (
    <QueryClientProvider client={client}>
      <I18nProvider i18n={i18n}>
        <PreviewDialog {...props} />
      </I18nProvider>
    </QueryClientProvider>
  );
}
function PreviewDialog({
  surface,
  state,
  platform,
  connection,
  device,
  destination,
}: z.infer<typeof schema>) {
  const { t } = useLingui();
  const { clearDialog } = usePostNotificationNavigation();
  const [open, setOpen] = useState(true);
  const search = Route.useSearch();
  const settingsHref = `${window.location.pathname}?${new URLSearchParams({
    ...Object.fromEntries(Object.entries(search).map(([key, value]) => [key, String(value)])),
    surface: 'general',
  })}`;
  const deviceStatus: PushDeviceStatus =
    device ??
    (state === 'blocked' || state === 'native-denied'
      ? 'denied'
      : state === 'unregistered'
        ? 'no-token-on-server'
        : state === 'unsupported'
          ? 'unsupported'
          : state === 'prompt'
            ? 'prompt'
            : state === 'unknown'
              ? 'unknown'
              : 'ok');
  const delivery = (
    <MatchAlertChannelsContent
      preferences={
        state === 'loading' || state === 'error'
          ? null
          : ['BELL', 'PUSH', 'DISCORD_DM'].map((channelType) => ({
              channelType,
              enabled:
                state !== 'off' &&
                !(state === 'push-off' && channelType === 'PUSH') &&
                !(state === 'bell-off' && channelType === 'BELL') &&
                !(state === 'discord-off' && channelType === 'DISCORD_DM'),
            }))
      }
      pending={state === 'loading'}
      error={state === 'error'}
      saving={false}
      deviceStatus={deviceStatus}
      discordLinked={
        state === 'unknown' || connection === 'unknown'
          ? null
          : state !== 'no-discord' && connection !== 'unlinked'
      }
      native={platform !== 'web' || state === 'native-denied'}
      onRetry={noop}
      onToggle={noop}
      onSetupPush={noop}
      onOpenSystemSettings={noop}
      onConnectDiscord={noop}
    />
  );
  if (surface === 'general')
    return (
      <SettingsLayout>
        <NotificationSettingsPage>
          <NotificationChannels />
        </NotificationSettingsPage>
      </SettingsLayout>
    );
  return (
    <DialogRoot
      size="md"
      open={open}
      onOpenChange={({ open: next }) => {
        if (!next) clearDialog();
        setOpen(next);
      }}
    >
      <DialogContent maxH="90dvh" overflowY="auto">
        <DialogHeader
          title={surface === 'intro' ? t`Your post is live` : t`Post settings`}
          closable
        />
        <Dialog.Body pb={6} display="flex" flexDirection="column" gap={5}>
          {surface === 'settings' && <SectionHeading>{t`Finding players`}</SectionHeading>}
          {surface === 'channels' ? (
            delivery
          ) : (
            <MatchAlertsRow
              postId="preview-post"
              push={previewPush}
              delivery={delivery}
              surface={surface === 'intro' ? 'intro' : 'settings'}
              settingsHref={destination === 'preview' ? settingsHref : undefined}
            />
          )}
          {surface === 'intro' && (
            <Button
              size="lg"
              w="100%"
              onClick={() => {
                clearDialog();
                setOpen(false);
              }}
            >{t`Got it`}</Button>
          )}
        </Dialog.Body>
      </DialogContent>
    </DialogRoot>
  );
}
